import { Quaternion, Vector3, type Bone, type Skeleton } from 'three'
import { QuaternionOneEuroFilter } from './OneEuroFilter'

/**
 * Drives a Three.js skeleton from MediaPipe Pose Landmarker world-landmarks output.
 *
 * MediaPipe BlazePose returns 33 landmarks; we only use the 14 body-pose ones (no
 * face, no fingers — those need Holistic, which is much heavier and out of scope
 * for a hobbyist live-driving tool).
 *
 * Approach:
 *   1. For each driven bone, compute a target world-space direction from a pair of
 *      landmarks (e.g. upperarm_l targets the direction from left-shoulder to left-elbow).
 *   2. Convert the target direction into the bone's parent's local frame.
 *   3. Solve the local rotation that takes the bone's bind-pose local direction
 *      (read once from the skeleton) to the target.
 *   4. Apply through a per-bone OneEuroFilter (renormalized componentwise) to suppress
 *      MediaPipe's per-frame jitter without adding visible lag at speed.
 *   5. Walk bones in hierarchy order (parents before children) so each bone's rotation
 *      is computed against an up-to-date parent world rotation.
 *
 * What's NOT solved here:
 *   - Twist along the bone axis (a single direction underdetermines rotation by one
 *     degree of freedom). Live mocap from a single camera generally can't resolve
 *     this anyway. Leave bones at their bind-pose twist.
 *   - Hand fingers (no landmarks).
 *   - Face / facial expression.
 *   - Hip translation (root motion). v1 keeps the rig planted and rotates only.
 */

/** MediaPipe BlazePose landmark indices we use. */
export const PoseLandmarkIndex = {
  NOSE: 0,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32
} as const

/** A single normalized 3D landmark (MediaPipe coordinate system). */
export interface Landmark {
  x: number
  y: number
  z: number
  visibility?: number
}

/**
 * Definition of which landmark(s) drive each rig bone.
 *
 * `from_landmark` / `to_landmark` accept either a single landmark index OR an
 * array. When an array is provided, the positions are averaged to form a
 * synthetic landmark — used for "midpoint" anchors like the hip center
 * (avg of LEFT_HIP + RIGHT_HIP) and shoulder center (avg of L+R shoulders).
 * That's how we drive the spine without a dedicated hip-center landmark.
 */
export interface BoneDriveSpec {
  /** Mesh2Motion / Meshinator bone name. */
  bone_name: string
  /** Landmark index(es) representing the bone's start. Array members are averaged. */
  from_landmark: number | number[]
  /** Landmark index(es) the bone should point toward. Array members are averaged. */
  to_landmark: number | number[]
}

/** Pair of indices used by the synthetic-landmark "average" form. */
const HIP_CENTER = [PoseLandmarkIndex.LEFT_HIP, PoseLandmarkIndex.RIGHT_HIP]
const SHOULDER_CENTER = [PoseLandmarkIndex.LEFT_SHOULDER, PoseLandmarkIndex.RIGHT_SHOULDER]

/**
 * Default driving spec for the Meshinator humanoid skeleton.
 *
 * Order matters: parents before children so each bone's update sees an up-to-date
 * parent world transform.
 *
 * Bones intentionally not driven:
 * - `head` — driving head from nose alone tilts unnaturally; let it inherit neck rotation
 * - `head_leaf`, `ball_l/r`, `ball_leaf_l/r` — leaf/tip bones, no useful target
 * - all finger bones — `PoseLandmarker` has no finger landmarks. Would require
 *   `HandLandmarker` / `Holistic` (separate, heavier model)
 * - `pelvis` rotation — leaving pelvis at bind pose keeps the rig planted; spine
 *   bones do the visible torso bending
 */
export const DEFAULT_HUMANOID_DRIVE_SPEC: BoneDriveSpec[] = [
  // Spine: drive spine_01 (lumbar) so the bend happens at the lower spine —
  // visually natural when the user leans forward/back/sideways. spine_02 and
  // spine_03 stay at bind pose; the upper torso rides along on spine_01's rotation.
  { bone_name: 'spine_01', from_landmark: HIP_CENTER, to_landmark: SHOULDER_CENTER },

  // Neck: shoulder-midpoint → nose. Captures head pitch + side-tilt approximately.
  // The actual `head` bone stays at bind pose — driving it from a single nose
  // landmark adds spurious pitch (nose isn't above the neck-base).
  { bone_name: 'neck_01', from_landmark: SHOULDER_CENTER, to_landmark: PoseLandmarkIndex.NOSE },

  // Arms: shoulder → elbow → wrist
  { bone_name: 'upperarm_l', from_landmark: PoseLandmarkIndex.LEFT_SHOULDER, to_landmark: PoseLandmarkIndex.LEFT_ELBOW },
  { bone_name: 'lowerarm_l', from_landmark: PoseLandmarkIndex.LEFT_ELBOW, to_landmark: PoseLandmarkIndex.LEFT_WRIST },
  { bone_name: 'upperarm_r', from_landmark: PoseLandmarkIndex.RIGHT_SHOULDER, to_landmark: PoseLandmarkIndex.RIGHT_ELBOW },
  { bone_name: 'lowerarm_r', from_landmark: PoseLandmarkIndex.RIGHT_ELBOW, to_landmark: PoseLandmarkIndex.RIGHT_WRIST },

  // Legs: hip → knee → ankle → foot
  { bone_name: 'thigh_l', from_landmark: PoseLandmarkIndex.LEFT_HIP, to_landmark: PoseLandmarkIndex.LEFT_KNEE },
  { bone_name: 'calf_l', from_landmark: PoseLandmarkIndex.LEFT_KNEE, to_landmark: PoseLandmarkIndex.LEFT_ANKLE },
  { bone_name: 'foot_l', from_landmark: PoseLandmarkIndex.LEFT_ANKLE, to_landmark: PoseLandmarkIndex.LEFT_FOOT_INDEX },
  { bone_name: 'thigh_r', from_landmark: PoseLandmarkIndex.RIGHT_HIP, to_landmark: PoseLandmarkIndex.RIGHT_KNEE },
  { bone_name: 'calf_r', from_landmark: PoseLandmarkIndex.RIGHT_KNEE, to_landmark: PoseLandmarkIndex.RIGHT_ANKLE },
  { bone_name: 'foot_r', from_landmark: PoseLandmarkIndex.RIGHT_ANKLE, to_landmark: PoseLandmarkIndex.RIGHT_FOOT_INDEX }
]

/**
 * Convert a MediaPipe world-coordinate landmark (x right, y down, z away-from-camera)
 * into Three.js world space (x right, y up, z toward viewer).
 */
export function media_to_three (lm: Landmark): Vector3 {
  return new Vector3(lm.x, -lm.y, -lm.z)
}

/**
 * Resolve a `BoneDriveSpec.from/to_landmark` (single index OR array of indices)
 * into a single Three.js world-space position. Arrays are averaged. Returns true
 * on success and writes into `out`; returns false if any landmark is missing or
 * fails the visibility threshold.
 */
function resolve_landmark (
  landmarks: Landmark[],
  spec: number | number[],
  min_visibility: number,
  out: Vector3
): boolean {
  if (typeof spec === 'number') {
    const lm = landmarks[spec]
    if (lm === undefined) return false
    if ((lm.visibility ?? 1) < min_visibility) return false
    out.set(lm.x, -lm.y, -lm.z)
    return true
  }
  // Array form: average the resolved positions. All members must pass the
  // visibility threshold — partial visibility on one of two endpoints would
  // bias the synthetic landmark in a misleading direction.
  let sum_x = 0; let sum_y = 0; let sum_z = 0; let count = 0
  for (const idx of spec) {
    const lm = landmarks[idx]
    if (lm === undefined) return false
    if ((lm.visibility ?? 1) < min_visibility) return false
    sum_x += lm.x; sum_y += lm.y; sum_z += lm.z; count++
  }
  if (count === 0) return false
  out.set(sum_x / count, -sum_y / count, -sum_z / count)
  return true
}

/** Visibility threshold below which a landmark is considered unreliable. */
export const DEFAULT_VISIBILITY_THRESHOLD = 0.5

/**
 * Compute the minimum visibility across a drive spec's landmark indices. Used
 * for confidence-weighted smoothing: low-confidence samples get dampened
 * inside the filter rather than snap-rejected.
 */
function spec_min_visibility (landmarks: Landmark[], spec: number | number[]): number {
  if (typeof spec === 'number') {
    const lm = landmarks[spec]
    return lm?.visibility ?? 1
  }
  let min = 1
  for (const idx of spec) {
    const lm = landmarks[idx]
    const v = lm?.visibility ?? 1
    if (v < min) min = v
  }
  return min
}

/**
 * Per-bone state cached across frames: the bind-pose local direction (constant),
 * the bind-pose local rotation (constant; captured at bind time so we can
 * restore to it when the bone is disabled), and a per-bone quaternion smoothing
 * filter (stateful).
 */
interface BoneCache {
  bone: Bone
  rest_local_dir: Vector3
  rest_local_quaternion: { x: number, y: number, z: number, w: number }
  filter: QuaternionOneEuroFilter
}

export interface PoseToRigOptions {
  /** Override the default humanoid drive spec. Bones not in the spec stay at bind pose. */
  drive_specs?: BoneDriveSpec[]
  /** Visibility threshold for landmarks (0..1). Skipping unreliable bones avoids snap-glitches. */
  visibility_threshold?: number
  /** Filter parameters for per-bone quaternion smoothing. */
  filter_params?: { min_cutoff?: number, beta?: number, d_cutoff?: number }
  /**
   * Bone whose XZ position should be driven from the hip-midpoint landmark when
   * root motion is enabled. Typically `pelvis` for humanoids. If not set, root
   * motion is unavailable regardless of the enabled flag.
   */
  root_motion_bone_name?: string
  /**
   * Confidence-weighted smoothing strength (0..2; default 0 = behavior matches
   * pre-feature). Each per-bone smoothing alpha is scaled by
   * `min(visibility) ^ smoothing_strength`. At strength 1, a landmark with
   * visibility 0.5 gets half the smoothing alpha (more dampening). At 0, the
   * filter is unchanged. Higher values aggressively dampen unreliable frames.
   */
  smoothing_strength?: number
}

export class PoseToRig {
  private readonly drive_specs: BoneDriveSpec[]
  private readonly visibility_threshold: number
  private readonly filter_params: PoseToRigOptions['filter_params']
  private readonly root_motion_bone_name: string | null
  private bone_cache: BoneCache[] | null = null
  /**
   * Per-bone enable mask. A bone is driven when its name is NOT in this set.
   * Empty by default = drive everything in the spec.
   */
  private disabled_bones: Set<string> = new Set()
  /** Root-motion runtime state. Calibrated on the first valid hip-midpoint landmark after enabling. */
  private root_motion_enabled: boolean = false
  private root_motion_bone: Bone | null = null
  private root_motion_rest_position: Vector3 | null = null
  private root_motion_reference: Vector3 | null = null
  private smoothing_strength: number = 0

  constructor (options: PoseToRigOptions = {}) {
    this.drive_specs = options.drive_specs ?? DEFAULT_HUMANOID_DRIVE_SPEC
    this.visibility_threshold = options.visibility_threshold ?? DEFAULT_VISIBILITY_THRESHOLD
    this.filter_params = options.filter_params
    this.root_motion_bone_name = options.root_motion_bone_name ?? null
    this.smoothing_strength = Math.max(0, options.smoothing_strength ?? 0)
  }

  /**
   * Adjust the confidence-weighted smoothing strength at runtime. Lets the
   * mocap settings dialog tune the dial live without restarting the session.
   * Clamped to [0, 4]; values above ~2 effectively freeze low-confidence bones.
   */
  set_smoothing_strength (strength: number): void {
    if (!Number.isFinite(strength)) return
    this.smoothing_strength = Math.max(0, Math.min(4, strength))
  }

  get_smoothing_strength (): number {
    return this.smoothing_strength
  }

  set_root_motion_enabled (enabled: boolean): void {
    if (this.root_motion_enabled === enabled) return
    this.root_motion_enabled = enabled
    // Always reset the reference on toggle so the next first-frame recalibrates.
    this.root_motion_reference = null
    if (!enabled) this.snap_root_motion_bone_to_rest()
  }

  is_root_motion_enabled (): boolean {
    return this.root_motion_enabled
  }

  private snap_root_motion_bone_to_rest (): void {
    if (this.root_motion_bone === null || this.root_motion_rest_position === null) return
    this.root_motion_bone.position.copy(this.root_motion_rest_position)
  }

  /** All bones that this PoseToRig instance KNOWS HOW TO drive (regardless of current enable state). */
  get_drivable_bone_names (): string[] {
    return this.drive_specs.map(s => s.bone_name)
  }

  /** Returns true when the named bone will be driven on the next update. */
  is_bone_enabled (bone_name: string): boolean {
    return !this.disabled_bones.has(bone_name)
  }

  /**
   * Enable or disable driving for a named bone. When disabled, the bone returns
   * to its bind-pose rotation immediately on the next call to `update()`.
   * Calling with a bone name not in the spec is a no-op.
   */
  set_bone_enabled (bone_name: string, enabled: boolean): void {
    if (enabled) {
      this.disabled_bones.delete(bone_name)
    } else {
      this.disabled_bones.add(bone_name)
      // Reset filter for this bone so re-enabling later doesn't snap from a stale value.
      const cached = this.bone_cache?.find(c => c.bone.name === bone_name)
      cached?.filter.reset()
      // Snap the bone back to its bind-pose local rotation (captured at bind() time).
      if (cached !== undefined) {
        const r = cached.rest_local_quaternion
        cached.bone.quaternion.set(r.x, r.y, r.z, r.w)
        cached.bone.updateMatrixWorld(true)
      }
    }
  }

  /** Bulk replacement of the disabled set. */
  set_disabled_bones (names: string[]): void {
    this.disabled_bones = new Set(names)
  }

  get_disabled_bones (): string[] {
    return Array.from(this.disabled_bones).sort()
  }

  /**
   * Bind to a skeleton. Captures bind-pose local directions for each driven bone
   * by reading the position of the FIRST child bone (which is always the bone's
   * "forward" direction in Three.js's bone-as-parent representation).
   *
   * If a bone is missing from the skeleton, it's silently skipped — useful when
   * the rig has fewer joints than our spec assumes (e.g. Tripo without fingers,
   * which we don't drive anyway).
   */
  bind (skeleton: Skeleton): void {
    const bone_by_name = new Map(skeleton.bones.map(b => [b.name, b]))
    this.bone_cache = []

    // Snapshot the root-motion bone (if configured). Reference landmark stays
    // null until the first valid frame so the first hip-midpoint becomes the
    // origin and subsequent frames apply deltas relative to it.
    if (this.root_motion_bone_name !== null) {
      const bone = bone_by_name.get(this.root_motion_bone_name) ?? null
      this.root_motion_bone = bone
      this.root_motion_rest_position = bone !== null ? bone.position.clone() : null
      this.root_motion_reference = null
    }

    for (const spec of this.drive_specs) {
      const bone = bone_by_name.get(spec.bone_name)
      if (bone === undefined) continue

      const child = bone.children.find((c): c is Bone => (c as Bone).isBone === true)
      if (child === undefined) continue

      const rest_local_dir = child.position.clone()
      if (rest_local_dir.lengthSq() < 1e-12) continue
      rest_local_dir.normalize()

      this.bone_cache.push({
        bone,
        rest_local_dir,
        rest_local_quaternion: {
          x: bone.quaternion.x,
          y: bone.quaternion.y,
          z: bone.quaternion.z,
          w: bone.quaternion.w
        },
        filter: new QuaternionOneEuroFilter(this.filter_params)
      })
    }
  }

  /**
   * Apply one frame of landmarks to the bound skeleton. Caller is responsible for
   * scheduling — typically inside the render loop, gated on mocap mode being active.
   *
   * @param landmarks 33-element array of MediaPipe world landmarks (already in meters).
   * @param time Current timestamp in seconds (used by the smoothing filter).
   */
  update (landmarks: Landmark[], time: number): void {
    if (this.bone_cache === null) return

    const cache = this.bone_cache
    const min_visibility = this.visibility_threshold
    const _from_world = new Vector3()
    const _to_world = new Vector3()
    const _v_target_world = new Vector3()
    const _v_target_local = new Vector3()
    const _q_target_local = new Quaternion()
    const _q_parent_world_inv = new Quaternion()

    // Walk in the order specs were registered so parents are updated before children.
    for (let i = 0; i < this.drive_specs.length; i++) {
      const spec = this.drive_specs[i]
      if (this.disabled_bones.has(spec.bone_name)) continue
      const cached = cache.find(c => c.bone.name === spec.bone_name)
      if (cached === undefined) continue

      const from_resolved = resolve_landmark(landmarks, spec.from_landmark, min_visibility, _from_world)
      if (!from_resolved) continue
      const to_resolved = resolve_landmark(landmarks, spec.to_landmark, min_visibility, _to_world)
      if (!to_resolved) continue

      // World-space target direction
      _v_target_world.copy(_to_world).sub(_from_world)
      if (_v_target_world.lengthSq() < 1e-9) continue
      _v_target_world.normalize()

      // Convert into parent's local frame so we can solve for the bone's local rotation.
      // Make sure parent's world matrix reflects any earlier-in-this-loop updates.
      const parent = cached.bone.parent
      if (parent === null) continue
      parent.updateWorldMatrix(true, false)
      parent.getWorldQuaternion(_q_parent_world_inv).invert()

      _v_target_local.copy(_v_target_world).applyQuaternion(_q_parent_world_inv).normalize()
      _q_target_local.setFromUnitVectors(cached.rest_local_dir, _v_target_local)

      // Confidence weight: min visibility across the spec's landmarks, raised
      // to the smoothing_strength power. weight=1 leaves the filter unchanged;
      // values < 1 dampen this frame's contribution.
      let confidence_weight = 1
      if (this.smoothing_strength > 0) {
        const min_vis = Math.min(
          spec_min_visibility(landmarks, spec.from_landmark),
          spec_min_visibility(landmarks, spec.to_landmark)
        )
        confidence_weight = Math.pow(Math.max(0, Math.min(1, min_vis)), this.smoothing_strength)
      }

      // Smoothed write
      const smoothed = cached.filter.filter(
        [_q_target_local.x, _q_target_local.y, _q_target_local.z, _q_target_local.w],
        time,
        confidence_weight
      )
      cached.bone.quaternion.set(smoothed[0], smoothed[1], smoothed[2], smoothed[3])
      cached.bone.updateMatrixWorld(true)
    }

    // Optional XZ root motion. Drive the configured bone's XZ position from the
    // hip-midpoint landmark, relative to a reference captured on the first valid
    // frame. Y stays at rest (vertical jump tracking is unreliable from a
    // monocular webcam, and a rig that suddenly hops looks broken).
    if (this.root_motion_enabled && this.root_motion_bone !== null && this.root_motion_rest_position !== null) {
      const _hip = new Vector3()
      const ok = resolve_landmark(landmarks, HIP_CENTER, min_visibility, _hip)
      if (ok) {
        if (this.root_motion_reference === null) {
          this.root_motion_reference = _hip.clone()
        } else {
          const dx = _hip.x - this.root_motion_reference.x
          const dz = _hip.z - this.root_motion_reference.z
          this.root_motion_bone.position.set(
            this.root_motion_rest_position.x + dx,
            this.root_motion_rest_position.y,
            this.root_motion_rest_position.z + dz
          )
          this.root_motion_bone.updateMatrixWorld(true)
        }
      }
    }
  }

  /** Reset all per-bone smoothing filters. Call when re-acquiring the webcam stream. */
  reset_filters (): void {
    if (this.bone_cache === null) return
    for (const c of this.bone_cache) c.filter.reset()
  }

  /** True if `bind()` has been called and at least one driven bone was found. */
  is_bound (): boolean {
    return this.bone_cache !== null && this.bone_cache.length > 0
  }
}
